import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../../../context/ThemeContext';
import * as d3 from 'd3';
import { useTranslation } from "react-i18next";

const TreeChart = ({ data, user }) => {
    const { theme } = useTheme();
    const svgRef = useRef();
    const [svgHeight, setSvgHeight] = useState(600);
    const { t } = useTranslation();

    useEffect(() => {
        const svg = d3.select(svgRef.current);
        const width = 1250;
        const margin = { top: 30, right: 0, bottom: 0, left: 0 };

        const innerWidth = width - margin.left - margin.right;

        const getChildrenArray = (children) => {
            if (!children) return [];
            return Array.isArray(children) ? children : Object.values(children);
        };

        const isDisplayableValue = (value) => {
            const normalized = String(value || '').trim();
            return normalized
                && normalized !== 'NA'
                && normalized.toLowerCase() !== 'null'
                && normalized.toLowerCase() !== 'null null'
                && normalized.toLowerCase() !== 'undefined undefined';
        };

        const getCallingLineLabel = (call) => {
            if (isDisplayableValue(call?.callinglineid)) return call.callinglineid;
            return 'N/A';
        };

        const getCalledLineLabel = (call) => {
            if (isDisplayableValue(call?.calledlineid)) return call.calledlineid;
            if (isDisplayableValue(call?.user)) return call.user;
            return 'N/A';
        };

        const getBusyReasonLabel = (call) => {
            const reasons = [
                call?.calloutcomereason,
                call?.originalreason,
                call?.redirectreason,
                call?.relatedreason
            ].filter(Boolean);

            return reasons.find(reason => String(reason).toLowerCase().includes('busy')) || '';
        };

        const getNodeDisplayKey = (node) => {
            const call = node?.calls?.[0];
            if (!call) return '';

            return [
                String(call.callednumber || '').trim(),
                String(getCalledLineLabel(call) || '').trim(),
                String(call.starttime || '').trim()
            ].join('|');
        };

        const mergeNode = (target, source) => {
            target.calls = [...(target.calls || []), ...(source.calls || [])];
            target.children = [...getChildrenArray(target.children), ...getChildrenArray(source.children)];
            return target;
        };

        const dedupeNodesByDisplayKey = (nodes) => {
            const mergedNodes = [];
            const nodesByKey = new Map();

            nodes.forEach(node => {
                const key = getNodeDisplayKey(node);

                if (key && nodesByKey.has(key)) {
                    mergeNode(nodesByKey.get(key), node);
                    return;
                }

                if (key) {
                    nodesByKey.set(key, node);
                }
                mergedNodes.push(node);
            });

            return mergedNodes;
        };

        const filterHierarchy = (node) => {
            if (!node) return null;
            node.calls = Array.isArray(node.calls) ? node.calls.filter(Boolean) : [];

            const children = getChildrenArray(node.children);
            if (children.length > 0) {
                node.children = children
                    .map(filterHierarchy)
                    .filter(child => child !== null && (child.calls.length > 0 || child.children));

                node.children = dedupeNodesByDisplayKey(node.children);

                const nodeKey = getNodeDisplayKey(node);
                if (nodeKey) {
                    node.children = getChildrenArray(node.children).filter(child => {
                        if (getNodeDisplayKey(child) !== nodeKey) {
                            return true;
                        }

                        mergeNode(node, child);
                        return false;
                    });
                }
            }

            return node.calls.length > 0 || (node.children && node.children.length > 0) ? node : null;
        };

        const filteredData = filterHierarchy(JSON.parse(JSON.stringify(data))); // Deep copy data
        if (!filteredData) {
            svg.selectAll("*").remove();
            return;
        }

        const root = d3.hierarchy(filteredData, d => d.children);
        const treeLayout = d3.tree().size([innerWidth, svgHeight - margin.top - margin.bottom]);
        treeLayout(root);

        svg.selectAll("*").remove();

        const g = svg.append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        const tooltip = d3.select("body").append("div")
            .style("position", "absolute")
            .style("background", "#fff")
            .style("border", "1px solid #ccc")
            .style("padding", "10px")
            .style("border-radius", "5px")
            .style("box-shadow", "0px 0px 2px rgba(0, 0, 0, 0.2)")
            .style("pointer-events", "none")
            .style("opacity", 0);

        // Draw links between the valid nodes
        g.selectAll('.link')
            .data(root.links())
            .enter()
            .append('path')
            .attr('class', 'link')
            .attr('d', d3.linkHorizontal()
                .x(d => d.x)
                .y(d => d.y))
            .attr('stroke', d => {
                const sourceCall = d.source.data.calls ? d.source.data.calls[0] : undefined;  // Appel associé au noeud source
                const targetCall = d.target.data.calls ? d.target.data.calls[0] : undefined;  // Appel associé au noeud cible

                // Fonction pour vérifier si un appel est valide (Yes ou Yes-PostRedirection)
                const isCallValid = call => call && (call.answerindicator === 'Yes' || call.answerindicator === 'Yes-PostRedirection');

                // Si le noeud source est valide ou si c'est un point de liaison menant à un noeud valide
                return (isCallValid(sourceCall) || !sourceCall) && (isCallValid(targetCall) || !targetCall)
                    ? '#AEC353' // Vert si la condition est satisfaite
                    : '#ccc';   // Couleur par défaut sinon
            })
            .attr('fill', 'none');

        // Draw nodes
        const nodes = g.selectAll('.node')
            .data(root.descendants())
            .enter()
            .append('g')
            .attr('class', 'node')
            .attr('transform', d => `translate(${d.x},${d.y})`);

        // Add circles for each node
        nodes.append('circle')
            .attr('r', 5)
            .attr('fill', d => {
                const call = d.data.calls[0];
                return call ? (call.answerindicator === 'Yes' || call.answerindicator === 'Yes-PostRedirection' ? '#AEC353' : '#ccc') : '#ccc';
            })
            .on('mouseover', function (event, d) {
                const call = d.data.calls[0];

                if (!call) return;
                call.callinglineid = getCallingLineLabel(call);
                call.calledlineid = getCalledLineLabel(call);

                const calloutOutcomeClass = call.calloutcome === 'Success' ? 'tooltip-webex-legs-success' :
                    call.calloutcome === 'Failure' ? 'tooltip-webex-legs-failure' :
                        'tooltip-webex-legs-refusal';
                const answerIndicatorClass = call.answerindicator === 'Yes' ? 'tooltip-webex-legs-success' :
                    call.answerindicator === 'Yes-PostRedirection' ? 'tooltip-webex-legs-redirect' :
                        'tooltip-webex-legs-refusal';
                const directionIndicatorClass = call.direction === 'ORIGINATING' ? 'tooltip-webex-legs-redirect' : 'tooltip-webex-legs-refusal';
                // <p>Final Remote Id: <span> ${call.finalremotesessionid}</span></p>
                // <p>Local Session Id: <span> ${call.localcallid}</span></p>

                // <p>Remote Call Id: <span> ${call.remotecallid}</span></p>
                // 
                tooltip.transition().duration(200).style("opacity", 1);
                tooltip.html(`
                    <div class="tooltip-webex-legs-content">
                     <p>${t('Raison de redirection')}: <span> ${call.relatedreason}</span></p>
                     ${getBusyReasonLabel(call) ? `<p>Busy: <span>${getBusyReasonLabel(call)}</span></p>` : ''}
                     <p>${t('Ligne appelante')}: ${getCallingLineLabel(call)}</p>
                <p>${t('Numéro appelant')}: ${call.callingnumber || 'N/A'}</p>
                ${user.holdingId === 33 ? `<p>${t('Numéro affiché')}: <span> ${call.calleridnumber}</span></p>` : ''}
                <p>${t('Ligne appelé')}: ${call.calledlineid || 'N/A'}</p>
                <p>${t('Numéro appelé')}: ${call.callednumber || 'N/A'}</p>
                <p>${t('Date de début')}: ${call.starttime || 'N/A'}</p>
                <p>${t('Localisation')}: ${call.location || 'N/A'}</p>
                <p>${t('Motif de redirection')}: ${call.redirectreason || 'N/A'}</p>
                <p>${t("Type d'appel")}: 
                    ${call.calltype === 'SIP_ENTERPRISE' ? t('Interne') :
                        call.calltype === 'SIP_NATIONAL' ? t('National') :
                            call.calltype === 'SIP_MOBILE' ? 'Mobile' :
                                call.calltype === 'UNKNOWN' ? t('Inconnu') :
                                    call.calltype === 'SIP_PREMIUM' ? 'Prenium' :
                                        call.calltype === 'SIP_INTERNATIONAL' ? t('International') :
                                            call.calltype === 'SIP_INBOUND' ? t('Entrant') :
                                                call.calltype === 'SIP_OPERATOR' ? t('Opérateur') :
                                                    call.calltype === 'SIP_TOLLFREE' ? t('Sans frais') :
                                                        call.calltype === 'SIP_MEETING' ? t('Réunion') : call.calltype
                                                            || 'NA'
                    }
                </p>
                <p>${t("Durée de communication")}: ${call.duration || 'N/A'} ${t("secondes")}</p>
                <p>${t("Durée d'attente")}: ${call.ringduration || 'N/A'} ${t("secondes")}</p>
                ${call.redirectingnumber ? `<p>${t("Numéro de redirection")}: ${call.redirectingnumber}</p>` : ''}
                <p class="${calloutOutcomeClass}">
                    ${t("Résultat de l'appel")}: 
                    <span> 
                        ${call.calloutcome === "Success" ? t("Succès") :
                        call.calloutcome === "Refusal" ? t("Refusé") :
                            call.calloutcome === "Failure" ? t("Échec") : 'NA'
                    }
                    </span>
                </p>
                <p class="${answerIndicatorClass}">
                    ${t("Indicateur de réponse")}: 
                    <span> 
                        ${call.answerindicator === "Yes" ? t("Oui") :
                        call.answerindicator === "No" ? t("Non") : t("Redirection-d'appel")
                            || 'NA'
                    }
                    </span>
                </p>
                <p class="${directionIndicatorClass}">Direction: <span> ${call.direction}</span></p>
                </div>
                `)
                    .style("left", (event.pageX + 10) + "px")
                    .style("top", (event.pageY + 10) + "px");
            })
            .on('mousemove', function (event) {
                tooltip.style("left", (event.pageX + 10) + "px")
                    .style("top", (event.pageY + 10) + "px");
            })
            .on('mouseout', function () {
                tooltip.transition().duration(200).style("opacity", 0);
            });

        // Add labels to each node with more detailed information
        nodes.append('text')
            .style('font-size', '11px')
            .style('fill', theme === 'dark' ? 'white' : 'black')
            .selectAll('tspan')
            .data(d => {
                const call = d.data.calls[0];
                if (!call) return [];
                const calledLineLabel = getCalledLineLabel(call);
                return [
                    call.callednumber ? call.callednumber : '',
                    calledLineLabel !== 'N/A' ? (calledLineLabel.length > 16 ? calledLineLabel.substring(0, 17) + '...' : calledLineLabel) : '',
                    call.starttime ? call.starttime : ''
                ].filter(line => line); // Filtrer les lignes vides
            })
            .enter()
            .append('tspan')
            .attr('x', 10) // Assurez-vous que cela correspond à l'attribut dx
            .attr('dy', (d, i) => 15) // Ajuster l'espacement vertical
            .text(d => d);

        // Update the SVG height dynamically based on the new content
        const updateSvgHeight = () => {
            const bbox = g.node().getBBox();
            setSvgHeight(Math.min(bbox.height, svgHeight)); // Add padding for safety
        };

        updateSvgHeight();

        // Clean up tooltips when the component unmounts
        return () => {
            tooltip.remove();
        };
    }, [data, theme]);

    return <svg ref={svgRef} width="100%" height={svgHeight + 50}></svg>;
};

export default TreeChart;
